import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { productService, Product } from '../modules/products/services/productService';
import './styles/productcatalogue.css';

const API_STATIC = (process.env.REACT_APP_API_URL ?? 'http://localhost:8000/api/v1').replace(/\/api\/v1.*$/, '');
const HOME_LIMIT = 8;

const SkeletonCard: React.FC = () => (
    <div className="pcat-skeleton">
        <div className="pcat-skeleton-img" />
        <div className="pcat-skeleton-body">
            <div className="pcat-skeleton-line" style={{ width: '40%' }} />
            <div className="pcat-skeleton-line" style={{ width: '80%' }} />
            <div className="pcat-skeleton-line" style={{ width: '55%' }} />
        </div>
    </div>
);

const ArrowRight: React.FC = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
);

const ProductCatalogueSection: React.FC = () => {
    const [products, setProducts]   = useState<Product[]>([]);
    const [categories, setCategories] = useState<string[]>([]);
    const [activeCategory, setActiveCategory] = useState<string>('All');
    const [loading, setLoading]     = useState(true);

    const fetchProducts = useCallback(async (category: string) => {
        setLoading(true);
        try {
            const query = category === 'All'
                ? { limit: HOME_LIMIT }
                : { limit: HOME_LIMIT, category };
            const res = await productService.getPublicAll(query);
            setProducts(res.data.products);
            if (res.data.categories?.length) {
                setCategories(res.data.categories);
            }
        } catch {
            setProducts([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProducts('All');
    }, [fetchProducts]);

    const handleTabClick = (cat: string) => {
        setActiveCategory(cat);
        fetchProducts(cat);
    };

    return (
        <section className="pcat-section">
            <div className="container">
                <span className="pcat-eyebrow">OUR PRODUCTS</span>
                <h2 className="pcat-title">Explore Our Craft Collection</h2>
                <p className="pcat-subtitle">
                    Handcrafted by skilled artisans — browse by category and discover quality at every price point.
                </p>

                {/* Category tabs */}
                {categories.length > 0 && (
                    <div className="pcat-tabs">
                        <button
                            className={`pcat-tab${activeCategory === 'All' ? ' active' : ''}`}
                            onClick={() => handleTabClick('All')}
                        >
                            All
                        </button>
                        {categories.map(cat => (
                            <button
                                key={cat}
                                className={`pcat-tab${activeCategory === cat ? ' active' : ''}`}
                                onClick={() => handleTabClick(cat)}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                )}

                {/* Product grid */}
                <div className="pcat-grid">
                    {loading
                        ? Array.from({ length: HOME_LIMIT }).map((_, i) => <SkeletonCard key={i} />)
                        : products.length === 0
                            ? (
                                <div className="pcat-empty">
                                    No products in this category yet. Check back soon!
                                </div>
                            )
                            : products.map(p => (
                                <div className="pcat-card" key={p._id}>
                                    <div className="pcat-card-img-wrap">
                                        <img
                                            src={`${API_STATIC}${p.image}`}
                                            alt={p.name}
                                            onError={e => {
                                                (e.target as HTMLImageElement).src =
                                                    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"%3E%3Crect width="200" height="200" fill="%23f0f4f8"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="14"%3ENo image%3C/text%3E%3C/svg%3E';
                                            }}
                                        />
                                    </div>
                                    <div className="pcat-card-body">
                                        <span className="pcat-card-category">{p.category}</span>
                                        <h3 className="pcat-card-name">{p.name}</h3>
                                        <p className="pcat-card-brand">{p.brand}</p>
                                        <div className="pcat-card-footer">
                                            <span className="pcat-card-price">
                                                ₹{p.price.toLocaleString('en-IN')}
                                            </span>
                                            <Link to={`/products/${p._id}`} className="pcat-card-btn">
                                                View <ArrowRight />
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))
                    }
                </div>

                {/* View all CTA */}
                <div className="pcat-cta-row">
                    <Link to="/products" className="pcat-cta-btn">
                        View All Products <ArrowRight />
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default ProductCatalogueSection;
